import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area,
} from 'recharts';
import { BarChart3, PieChart as PieIcon, TrendingUp } from 'lucide-react';

const PALETA = ['#7c3aed', '#2563eb', '#0891b2', '#c026d3', '#f59e0b'];

const Panel = ({ icon: Icon, titulo, subtitulo, children, testid }) => (
  <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5"
    data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4 text-violet-600 dark:text-violet-400" />
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{titulo}</h3>
    </div>
    {subtitulo && <p className="text-xs text-gray-500 mb-3">{subtitulo}</p>}
    {children}
  </div>
);

/** Gráficos del análisis de remitentes. Los colores de ejes siguen el tema del CRM. */
export const RemitentesCharts = ({ serie, granularidad, topRemitentes, distribucion, oscuro, formatMonto }) => {
  const ejes = oscuro ? '#9ca3af' : '#6b7280';
  const grilla = oscuro ? '#374151' : '#e5e7eb';
  const tooltipStyle = {
    backgroundColor: oscuro ? '#1f2937' : '#ffffff',
    border: `1px solid ${grilla}`,
    borderRadius: 10,
    fontSize: 12,
    color: oscuro ? '#f3f4f6' : '#111827',
  };
  const etiquetaPeriodo = { dia: 'por día', semana: 'por semana', mes: 'por mes' }[granularidad] || '';

  const topChart = (topRemitentes || []).map(r => ({
    name: (r.titular || 'Sin nombre').substring(0, 22),
    monto: r.total_enviado,
  }));
  const pieChart = (distribucion || []).filter(d => d.remitentes > 0)
    .map(d => ({ name: d.rango, value: d.remitentes, monto: d.monto }));

  return (
    <>
      <Panel icon={TrendingUp} titulo="Evolución del dinero recibido"
        subtitulo={`Monto y cantidad de comprobantes ${etiquetaPeriodo}`} testid="chart-evolucion">
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={serie || []}>
            <defs>
              <linearGradient id="gradMonto" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.55} />
                <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={grilla} vertical={false} />
            <XAxis dataKey="periodo" stroke={ejes} fontSize={11} tickMargin={6} />
            <YAxis stroke={ejes} fontSize={11}
              tickFormatter={(v) => v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : `${Math.round(v / 1000)}k`} />
            <Tooltip contentStyle={tooltipStyle}
              formatter={(value, name) => name === 'monto' ? [formatMonto(value), 'Monto'] : [value, 'Comprobantes']} />
            <Area type="monotone" dataKey="monto" stroke="#7c3aed" strokeWidth={2} fill="url(#gradMonto)" />
          </AreaChart>
        </ResponsiveContainer>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel icon={BarChart3} titulo="Top 10 remitentes"
          subtitulo="Quiénes mandaron más plata en el período" testid="chart-top-remitentes">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topChart} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={grilla} horizontal={false} />
              <XAxis type="number" stroke={ejes} fontSize={11}
                tickFormatter={(v) => v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : `${Math.round(v / 1000)}k`} />
              <YAxis dataKey="name" type="category" stroke={ejes} fontSize={11} width={130} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [formatMonto(v), 'Total enviado']} />
              <Bar dataKey="monto" fill="#7c3aed" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel icon={PieIcon} titulo="Remitentes por tamaño"
          subtitulo="Cuántos remitentes hay en cada rango de monto acumulado" testid="chart-distribucion">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pieChart} cx="50%" cy="50%" outerRadius={95} dataKey="value" labelLine={false}
                label={(p) => (
                  <text x={p.x} y={p.y} fill={ejes} fontSize={11} textAnchor={p.textAnchor}
                    dominantBaseline="central">
                    {`${p.name}: ${(p.percent * 100).toFixed(0)}%`}
                  </text>
                )}>
                {pieChart.map((entry, i) => <Cell key={entry.name} fill={PALETA[i % PALETA.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle}
                formatter={(v, _n, item) => [`${v} remitentes · ${formatMonto(item?.payload?.monto)}`, item?.payload?.name]} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>
    </>
  );
};

export default RemitentesCharts;
